import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Link2, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  Trash2,
  Copy,
  ExternalLink,
  Shield,
  List
} from 'lucide-react';
import { hasPageAccess } from '../utils/permissions';

const ShortUrls = () => {
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');
  const [url, setUrl] = useState('');
  const [shortUrls, setShortUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [generatedUrl, setGeneratedUrl] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  const tabs = [
    { id: 'generate', label: 'Generate Short URL', icon: Link2 },
    { id: 'list', label: 'Short URL List', icon: List }
  ];

  // Fetch short URLs list
  const fetchShortUrls = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/short-urls`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setShortUrls(data);
      } else if (response.status === 403) {
        setMessage({ type: 'error', text: 'Access denied. Admin or Manager privileges required.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch short URLs.' });
      }
    } catch (error) {
      console.error('Error fetching short URLs:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Authentication useEffect
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (token && !user) {
      return;
    }

    if (!token || !user) {
      setPageLoading(false);
      return;
    }

    if (!hasPageAccess(user, 'business.short_urls', ['admin', 'manager'])) {
      setPageLoading(false);
      return;
    }

    // User has access - fetch short URLs
    fetchShortUrls();
    setPageLoading(false);
  }, [token, user, authLoading]);

  // Load short URLs when list tab is active
  useEffect(() => {
    if (activeTab === 'list' && user && hasPageAccess(user, 'business.short_urls', ['admin', 'manager'])) {
      fetchShortUrls();
    }
  }, [activeTab, user, token]);

  // Check if user has access
  if (!authLoading && !hasPageAccess(user, 'business.short_urls', ['admin', 'manager'])) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Short URLs.</p>
        </div>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading Short URLs...</p>
        </div>
      </div>
    );
  }

  // Handle URL generation
  const handleGenerateUrl = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setMessage({ type: 'error', text: 'Please enter a URL' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    setGeneratedUrl(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/short-urls/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedUrl({
          original: data.original_url,
          short: data.short_url
        });
        setMessage({ type: 'success', text: data.message });
        setUrl('');
        // Refresh list if on list tab
        if (activeTab === 'list') {
          fetchShortUrls();
        }
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Error generating short URL:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (message.type === 'error') {
          setMessage({ type: '', text: '' });
        }
      }, 5000);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: 'Copied to clipboard!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      setMessage({ type: 'error', text: 'Failed to copy' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    }
  };

  // Delete short URL
  const handleDelete = async (shortUrlId) => {
    if (!window.confirm('Are you sure you want to delete this short URL?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/short-urls/${shortUrlId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Short URL deleted successfully!' });
        fetchShortUrls();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete short URL' });
      }
    } catch (error) {
      console.error('Error deleting short URL:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Inside the main card */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Short URLs
              </h2>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg flex items-center ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                )}
                <span>{message.text}</span>
              </div>
            )}

            {/* Tab Navigation - Styled like Business Info */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          isActive
                            ? 'border-theme-blue text-theme-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-3 sm:p-6">
            {/* Generate Tab */}
            {activeTab === 'generate' && (
              <div className="space-y-6">
                <form onSubmit={handleGenerateUrl} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter URL to Shorten
                    </label>
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://example.com/your-long-url"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Enter any URL (from this website or external sites like Google)
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-theme-blue text-white rounded-lg hover:bg-theme-blue disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-4 w-4 mr-2" />
                        Generate Short URL
                      </>
                    )}
                  </button>
                </form>

                {/* Generated URL Display */}
                {generatedUrl && (
                  <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Short URL</h3>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Original URL</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={generatedUrl.original}
                            readOnly
                            className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700"
                          />
                          <button
                            onClick={() => copyToClipboard(generatedUrl.original)}
                            className="p-2 text-gray-600 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                            title="Copy original URL"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Short URL</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={generatedUrl.short}
                            readOnly
                            className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm text-theme-blue font-medium"
                          />
                          <button
                            onClick={() => copyToClipboard(generatedUrl.short)}
                            className="p-2 text-gray-600 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                            title="Copy short URL"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <a
                            href={generatedUrl.short}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-600 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                            title="Open short URL"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* List Tab */}
            {activeTab === 'list' && (
              <div>
                {shortUrls.length === 0 ? (
                  <div className="text-center py-12">
                    <Link2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No short URLs generated yet</p>
                    <button
                      onClick={() => setActiveTab('generate')}
                      className="mt-4 text-theme-blue hover:text-blue-700 font-medium"
                    >
                      Generate your first short URL
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Original URL
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Short URL
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {shortUrls.map((shortUrl) => (
                          <tr key={shortUrl.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <span className="text-sm text-gray-900 truncate max-w-xs" title={shortUrl.original_url}>
                                  {shortUrl.original_url}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(shortUrl.original_url)}
                                  className="ml-2 p-1 text-gray-400 hover:text-theme-blue transition-colors"
                                  title="Copy"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <a
                                  href={shortUrl.short_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-theme-blue hover:text-blue-700 font-medium truncate max-w-xs"
                                  title={shortUrl.short_url}
                                >
                                  {shortUrl.short_url}
                                </a>
                                <button
                                  onClick={() => copyToClipboard(shortUrl.short_url)}
                                  className="ml-2 p-1 text-gray-400 hover:text-theme-blue transition-colors"
                                  title="Copy"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {formatDate(shortUrl.created_at)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => handleDelete(shortUrl.id)}
                                className="text-red-600 hover:text-red-700 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortUrls;
